<template>
  <div v-if="enabledReviews.length" class="gs-section">
    <div class="gs-section__toolbar">
      <section-search v-model="query" placeholder="Search blog reviews" />
      <p class="gs-section__count">{{ countLabel }}</p>
    </div>

    <div v-if="visibleReviews.length" class="gs-asset-grid gs-asset-grid--wide">
      <asset-card
        v-for="review in visibleReviews"
        :key="previewKey(review)"
        collapsible
        :title="review.name"
        :subtitle="review.title"
      >
        <template slot="toolbar">
          <div class="gs-segmented" role="group" aria-label="Body view">
            <button
              v-for="view in views"
              :key="view.value"
              type="button"
              class="gs-segmented__item"
              :class="{ 'is-current': viewFor(review) === view.value }"
              :aria-pressed="viewFor(review) === view.value ? 'true' : 'false'"
              :data-view="view.value"
              @click="setView(review, view.value)"
            >
              {{ view.title }}
            </button>
          </div>
        </template>

        <template slot="preview">
          <!-- The vendor's own post HTML, already personalised server side by
               `fetch-funnel-resource`. -->
          <div
            v-if="viewFor(review) === 'preview'"
            class="gs-asset-html"
            :class="'gs-preview-' + previewKey(review)"
            v-html="previewHtml[previewKey(review)]"
          ></div>
          <pre v-else-if="viewFor(review) === 'html'" class="gs-asset-code">{{ review.htmlBody }}</pre>
          <pre v-else class="gs-asset-text">{{ review.textBody }}</pre>
        </template>

        <template slot="actions">
          <!-- Both flavours carry the markup: a plain-text-only target (a
               forum box, a terminal) received the HTML from the legacy copy
               button too, and pasting the plain-text body there instead would
               silently drop every link and image. -->
          <copy-button
            :text="review.htmlBody"
            :html="review.htmlBody"
            label="Copy HTML"
            :title="$t('frontend_resources_copy_blog_review')"
          />
          <copy-button
            :text="review.textBody"
            label="Copy text"
            :title="$t('frontend_resources_copy_blog_review')"
          />
          <copy-button
            v-if="review.title"
            :text="review.title"
            :label="`${$t('frontend_copy')} ${$t('frontend_resources_title')}`"
            title="Copy the post title"
          />
        </template>
      </asset-card>
    </div>

    <div v-else class="gs-empty">
      <span class="gs-empty__icon"><gs-icon name="search" size="22" /></span>
      <p class="gs-empty__title">No blog reviews match “{{ query }}”</p>
      <p class="gs-empty__text">Search looks at the review name and the post title.</p>
    </div>
  </div>

  <div v-else class="gs-empty">
    <span class="gs-empty__icon"><gs-icon name="heading" size="22" /></span>
    <p class="gs-empty__title">{{ $t('frontend_resources_no_blog_review') }}</p>
    <p class="gs-empty__text">Ready-to-publish review posts from the vendor show up here.</p>
  </div>
</template>

<script>
import AssetCard from '@/components/promotools/AssetCard.vue';
import CopyButton from '@/components/promotools/CopyButton.vue';
import { previewKeyMap } from '@/components/promotools/previewKeys';
import { sanitiseHtml } from '@/components/promotools/sanitiseHtml';
import SectionSearch from '@/components/promotools/SectionSearch.vue';

export default {
  components: {
    AssetCard,
    CopyButton,
    SectionSearch,
  },
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      query: '',
      // Per-review body view, keyed by preview key. Null-prototype, so an
      // asset whose key is `__proto__` cannot reach Object.prototype instead
      // of its own entry.
      reviewViews: Object.create(null),
    };
  },
  computed: {
    // "View HTML" and "Text View" are the vendor-side labels these bodies have
    // always carried; only "Preview" is new, so only it is English.
    views() {
      return [
        { title: 'Preview', value: 'preview' },
        { title: this.$t('frontend_resources_view_html'), value: 'html' },
        { title: this.$t('frontend_resource_text_view'), value: 'text' },
      ];
    },
    enabledReviews() {
      return (this.funnelResource && this.funnelResource.blog_reviews
        ? this.funnelResource.blog_reviews.filter(review => review.enabled)
        : []);
    },
    visibleReviews() {
      const query = this.query.trim().toLowerCase();

      if (!query) {
        return this.enabledReviews;
      }

      return this.enabledReviews.filter(review => {
        const haystack = `${review.name || ''} ${review.title || ''}`.toLowerCase();
        return haystack.indexOf(query) !== -1;
      });
    },
    countLabel() {
      const shown = this.visibleReviews.length;
      const total = this.enabledReviews.length;
      return shown === total
        ? `${total} ${total === 1 ? 'review' : 'reviews'}`
        : `${shown} of ${total} reviews`;
    },
    previewKeys() {
      return previewKeyMap(this.enabledReviews);
    },
    // Vendor markup is previewed, never executed. Sanitising is a parse, so
    // it happens once per asset here rather than on every re-render; the map
    // is rebuilt whenever the funnel resource changes. The post's own
    // stylesheet is rewritten to match only inside this card's preview well.
    //
    // Only the reviews on screen are parsed. `previewKeys` still covers every
    // enabled review, so a card keeps the same key (and the same view choice)
    // while a search narrows the list around it.
    previewHtml() {
      return this.visibleReviews.reduce((map, review) => {
        const key = this.previewKeys.get(review);
        map[key] = sanitiseHtml(review.htmlBody, { scope: `gs-preview-${key}` });
        return map;
      }, Object.create(null));
    },
  },
  methods: {
    previewKey(review) {
      return this.previewKeys.get(review) || '';
    },
    viewFor(review) {
      return this.reviewViews[this.previewKey(review)] || 'preview';
    },
    setView(review, view) {
      this.$set(this.reviewViews, this.previewKey(review), view);
    },
  },
};
</script>
